import { Injectable } from '@angular/core';
import { FormGroup, FormControl, FormArray, AsyncValidatorFn } from '@angular/forms';
import { AbstractControl } from '@angular/forms';
import { Observable } from 'rxjs';

import { ApiService } from './api.service';
import { personNameChars, singleWordLength } from '../validators/person-name.validator';

/**
 * Reguły nowego hasła: błąd walidatora → zdanie, którym o nim mówimy użytkownikowi.
 * Kolejność wierszy jest kolejnością zdań w komunikacie.
 */
const NEW_PASSWORD_RULES: Array<{ error: string, message: string }> = [
    { error: 'minlength', message: 'FORMS.PASSWORD_RULES.TOO_SHORT' },
    { error: 'maxlength', message: 'FORMS.PASSWORD_RULES.TOO_LONG' },
    { error: 'upperRequired', message: 'FORMS.PASSWORD_RULES.NEEDS_UPPERCASE' },
    { error: 'lowRequired', message: 'FORMS.PASSWORD_RULES.NEEDS_LOWERCASE' },
    { error: 'numberRequired', message: 'FORMS.PASSWORD_RULES.NEEDS_DIGIT' },
    { error: 'checkCharsInPassword', message: 'FORMS.PASSWORD_RULES.NO_SPACES' },
];

@Injectable({ providedIn: 'root' })
export class ValidateService {

    constructor(
        private apiService: ApiService
    ) { }

    validateAllFormFields(formGroup: FormGroup | FormArray) {
        Object.keys(formGroup.controls).forEach(field => {
            const control = formGroup.get(field);
            if (control instanceof FormControl) {
                control.markAsTouched({ onlySelf: true });
            } else if ((control instanceof FormGroup) || (control instanceof FormArray)) {
                this.validateAllFormFields(control);
            }
        });
    }

    whCheckPhonesDuplicate(AC: AbstractControl) {
        const data = AC.value;
        const duplicates = [];
        const duplicates2 = [];

        for (let i = 0; i < data.length; i++) {
            if (data[i]) {
                if (duplicates.includes(data[i])) {

                    duplicates2.push(data[i]);

                    if (AC['duplicate'] != duplicates2) {
                        AC['duplicate'] = duplicates2;
                    }

                } else {

                    duplicates.push(data[i]);
                }
            }
        }

        return null;
    }

    emailValidation(AC: AbstractControl) {
      const control = AC.value;

      const regex = /^[a-zA-Z0-9]+([._+\-][a-zA-Z0-9]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;

      if (!control || control.endsWith('@sidly') || regex.test(control)) {
        return null;
      } else {
        return { emailValidation: true };
      }
    }

    dateValidator(c: AbstractControl): { [key: string]: boolean } {
        let value = c.value;
        if (value && typeof value === "string") {
            let match = value.match(/^([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/);
            if (!match) {
                return { 'dateInvalid': true };
            } else if (match && match[0] !== value) {
                return { 'dateInvalid': true };
            }
        }
        return null;
    }

    matchPassword(AC: AbstractControl) {
        const password = AC.get('password').value;
        const confirmPassword = AC.get('password2').value;
        if (password !== confirmPassword) {
            AC.get('password2').setErrors({ matchPassword: true });
        } else {
            AC.get('password2').setErrors(null);
            return null;
        }
    }

    matchPhones(AC: AbstractControl) {
        const number = AC.get('number').value;
        const confirmNumber = AC.get('repeatNumber').value;
        if (number !== confirmNumber) {
            AC.get('repeatNumber').setErrors({ matchPhones: true });
        } else {
            AC.get('repeatNumber').setErrors(null);
            return null;
        }
    }

    checkCharsInPassword(AC: AbstractControl) {
        const control = AC.get('password');

        if (control.value.indexOf(' ') === -1) {
            return null;
        }

        // Dokładamy do błędów pola, zamiast je podmieniać: setErrors() kasuje wynik
        // walidatorów samej kontrolki, więc hasło ze spacją i bez cyfry gubiło informację
        // o cyfrze. Walidatory grupy chodzą po walidatorach pola, więc jest co scalać.
        control.setErrors({ ...(control.errors || {}), checkCharsInPassword: true });
    }

    /**
     * Wszystko, co blokuje zapis formularza zmiany hasła — po jednym zdaniu na złamaną
     * regułę, w kolejności pól na ekranie.
     *
     * Lista, a nie pierwszy napotkany błąd: użytkownik, który dowiaduje się o brakującej
     * cyfrze dopiero po poprawieniu wielkiej litery, klika „Zapisz" tyle razy, ile reguł
     * złamał. Ogólne „popraw dane" nie mówiło nawet tego.
     */
    passwordFormProblems(form: AbstractControl): string[] {
        const stare = form.get('password');
        const nowe = form.get('passwords.password');
        const powtorzenie = form.get('passwords.password2');
        const problemy: string[] = [];

        if (stare && stare.invalid) {
            problemy.push(stare.hasError('required')
                ? 'FORMS.PASSWORD_RULES.OLD_REQUIRED'
                : 'FORMS.PASSWORD_RULES.OLD_INVALID');
        }

        if (nowe && nowe.invalid) {
            // Puste pole łamie wszystkie reguły naraz — wyliczanie ich byłoby krzykiem.
            if (nowe.hasError('required')) {
                problemy.push('FORMS.PASSWORD_RULES.NEW_REQUIRED');
            } else {
                NEW_PASSWORD_RULES
                    .filter(regula => nowe.hasError(regula.error))
                    .forEach(regula => problemy.push(regula.message));
            }
        }

        if (powtorzenie && powtorzenie.invalid) {
            problemy.push('FORMS.PASSWORD_IS_NOT_THE_SAME');
        }

        // Formularz odrzucony bez wskazania pola — lepiej powiedzieć cokolwiek niż nic.
        return problemy.length ? problemy : ['BACKEND.ALL_FIELDS_REQUIRED'];
    }

    /**
     * Zwraca WSZYSTKIE złamane reguły naraz. Wcześniej oddawał tylko pierwszą, więc hasło
     * bez cyfry i bez wielkiej litery wymagało dwóch podejść: użytkownik poprawiał cyfrę,
     * klikał „Zapisz" i dopiero wtedy dowiadywał się o literze.
     *
     * Kolejność kluczy jak dawniej (cyfra, wielka, mała) — komponenty pokazujące błąd pod
     * polem biorą pierwszy z brzegu i dla pojedynczej pomyłki mają pokazać to samo co przedtem.
     */
    passwordStrong(AC: AbstractControl) {
        const control = AC.value;
        const errors: { [key: string]: boolean } = {};

        if (!/\d/.test(control)) {
            errors.numberRequired = true;
        }
        if (!/[A-Z]/.test(control)) {
            errors.upperRequired = true;
        }
        if (!/[a-z]/.test(control)) {
            errors.lowRequired = true;
        }

        return Object.keys(errors).length ? errors : null;
    }

    minValidator(min: number) {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const input = Number(control.value);
            return isNaN(input) || input >= min ? null : { minIntValue: true };
        };
    }

    limitZerosValidator(maxZeros: number) {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const value = control.value ? control.value.toString() : '';
            const zerosCount = value.length;
            return zerosCount > maxZeros ? { limitZeros: { limitZeros: true, maxZeros: maxZeros } } : null;
        };
    }

    minLength(minChars: number) {
      return (control: AbstractControl): { [key: string]: any } | null => {
        const value = control.value ? control.value.toString() : '';
        const actualLength = value.length;
        return actualLength < minChars ? { minLengthV2: { minLengthV2: true, minChars: minChars } } : null;
      };
    }

  minValue(minValue: number) {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value ? parseFloat(control.value) : NaN;
      return isNaN(value) || value < minValue ? { minValueValidator: { minValueValidator: true, minValue: minValue } } : null;
    };
  }


  maxValue(maxValue: number) {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value ? parseFloat(control.value) : NaN;
      return isNaN(value) || value > maxValue ? { maxValueValidator: { maxValueValidator: true, maxValue: maxValue } } : null;
    };
  }

  maxLength(maxChars: number) {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value ? control.value.toString() : '';
      const actualLength = value.length;
      return actualLength > maxChars ? { maxLengthV2: { maxLengthV2: true, maxChars: maxChars } } : null;
    };
  }


  passwordNumberReq(AC: AbstractControl) {
        const control = AC.value;

        if (!(/\d/.test(control))) {
            return { numberRequired: true };
        }
    }

    passwordUpperReq(AC: AbstractControl) {
        const control = AC.value;

        if (!/[A-Z]/.test(control)) {
            return { upperRequired: true };
        }
    }

    passwordLowReq(AC: AbstractControl) {
        const control = AC.value;

        if (!(/[a-z]/.test(control))) {
            return { lowRequired: true };
        }
    }

    passwordLength(AC: AbstractControl) {
        const control = AC.value;

        if (control.length < 8) {
            return { passwordMinLength: true };
        }
    }

    checkHourFormat(AC: AbstractControl) {
        const control = AC.value;

        const pattern = /^([0-9][0-9]:[0-9][0-9])$/.test(control);

        if (control.length > 0) {

            if (control.length !== 5 || control[2] !== ':' || !pattern) {
                return { checkHoursFormat: true };
            } else {
                const hour = control[0] + control[1];
                const minutes = control[3] + control[4];

                if (!(hour >= 0 && hour < 24 && minutes >= 0 && minutes < 60)) {
                    return { badHoursFormat: true };
                } else if (hour === '23' && minutes === '59') {
                    return null;
                } else if (minutes % 15 !== 0) {
                    return { FifteenMinutesFormat: true };
                }
            }

        }

        return null;
    }

    checkHourFormatForEdit(AC: AbstractControl) {
        const control = AC.value;

        if (control) {
            const pattern = /^([0-9][0-9]:[0-9][0-9])$/.test(control);

            if (control.length > 0) {

                if (control.length !== 5 || control[2] !== ':' || !pattern) {
                    return { checkHoursFormat: true };
                } else {
                    const hour = control[0] + control[1];
                    const minutes = control[3] + control[4];

                    if (!(hour >= 0 && hour < 24 && minutes >= 0 && minutes < 60)) {
                        return { badHoursFormat: true };
                    }

                }

            }
        }

        return null;
    }


    checkHourGreater(AC: AbstractControl) {
        const control = AC.value;

        if (AC.get('startHour').status === 'VALID' && AC.get('endHour').status === 'VALID' &&
            AC.get('startSecondHour').status === 'VALID' && AC.get('endSecondHour').status === 'VALID') {
            if (control.startHour >= control.endHour && control.enabled1 ||
                control.startSecondHour >= control.endSecondHour && control.enabled2) {
                return { badStartHoursFormat: true };
            } else if (
                control.enabled1 && control.enabled2 && (
                    control.startHour >= control.startSecondHour && control.startHour <= control.endSecondHour ||
                    control.endHour >= control.startSecondHour && control.endHour <= control.endSecondHour ||
                    control.startSecondHour >= control.startHour && control.startSecondHour <= control.endHour ||
                    control.endSecondHour >= control.startHour && control.endSecondHour <= control.endHour
                )
            ) {
                return { badHoursFormat: true };
            }
        }

        return null;
    }

  checkHourEqualNightMode(AC: AbstractControl) {
    const nightStart = AC.get('night_start').value;
    const nightEnd = AC.get('night_stop').value;
    if (nightStart && nightEnd && nightStart === nightEnd && nightStart != '__:__') {
      return { equalStartAndEndHours: true };
    }

    return null;

  }

  checkHourGreaterEditUs(AC: AbstractControl) {
        const control = AC.value;

        if (AC.get('night_start').status === 'VALID' && AC.get('night_stop').status === 'VALID') {
            if (control.night_start >= control.night_stop) {
                return { badStartHoursFormat: true };
            }
        }

        return null;
    }



    checkCharsInNewPassword(AC: AbstractControl) {
        const password = AC.value;

        if (password.indexOf(' ') === -1) {
            return null;
        }
        return { checkCharsInNewPassword: true };

    }

    imeiValidation(AC: AbstractControl) {
      const imei = AC.value;

      if (!imei) {
        return { required: true };
      }

      if (imei < 100000000000000 || imei > 999999999999999) {
        return { imeiValidation: true };
      }

      return null;
    }

    peselValidation(AC: AbstractControl) {
      const pesel = AC.value;

      if (typeof pesel !== 'string' || !/^\d{11}$/.test(pesel)) {
        return { wrongPesel: true };
      }

      const weight = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
      let sum = 0;

      for (let i = 0; i < weight.length; i++) {
        sum += parseInt(pesel[i]) * weight[i];
      }

      const controlNumber = parseInt(pesel[10]);

      if ((10 - (sum % 10)) % 10 !== controlNumber) {
        return { wrongPesel: true };
      }

      return null;
    }

    peselValidationOptional(AC: AbstractControl) {
      const pesel = AC.value;
      if (!pesel || pesel === '') {
        return null;
      }

      if (typeof pesel !== 'string' || !/^\d{11}$/.test(pesel)) {
        return { wrongPesel: true };
      }

      const weight = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
      let sum = 0;
      for (let i = 0; i < weight.length; i++) {
        sum += parseInt(pesel[i]) * weight[i];
      }
      const controlNumber = parseInt(pesel[10]);
      if ((10 - (sum % 10)) % 10 !== controlNumber) {
        return { wrongPesel: true };
      }

      return null;
    }

    checkPhonesDuplicate(AC: AbstractControl) {
        const data = AC.value;
        const duplicates = [];
        const duplicates2 = [];

        for (let i = 0; i < data.length; i++) {
            if (data[i].number) {
                if (duplicates.includes(data[i].number)) {

                    duplicates2.push(data[i].number);

                    if (AC['duplicate'] != duplicates2) {
                        AC['duplicate'] = duplicates2;
                    }

                } else {

                    duplicates.push(data[i].number);
                }
            }
        }

        return null;
    }

    checkHoursDuplicate(AC: AbstractControl) {
        const data = AC.value;
        const sorted_data = data.slice(0).sort((a, b) => {
            if (a.hour < b.hour) {
                return 1;
            }
            if (a.hour > b.hour) {
                return -1;
            }
        });

        let results = [];
        for (let i = 0; i < sorted_data.length - 1; i++) {
            if (sorted_data[i].hour !== null && sorted_data[i].hour !== '') {
                if (sorted_data[i + 1].hour === sorted_data[i].hour) {
                    return { checkHoursDuplicate: true };
                }
            }
        }

    }

  checkLengthName(AC: AbstractControl) {
    return singleWordLength(AC);
  }


  checkNameChars(AC: AbstractControl) {
    return personNameChars(AC);
  }


  checkAreaCode(AC: AbstractControl) {
        const control = AC.value;

        if (control && control.length > 0) {
            if (control[0] !== '+') {
                return { checkAreaCode: true };
            }
        }
        return null;
    }

    checkMaximumPulse(AC: AbstractControl) {
        const control = AC.value;

        if (AC.get('minimum') && AC.get('maximum')) {
            if (control.minimum >= control.maximum) {
                AC.get('maximum').setErrors({ checkMaximumPulse: true });
            } else {
                AC.get('maximum').setErrors(null);
            }
        }
        return null;
    }

    checkMaxHr(AC: AbstractControl) {
        const control = AC.value;

        if (AC.get('minHr') && AC.get('maxHr')) {
            if (control.minHr >= control.maxHr) {
                AC.get('maxHr').setErrors({ checkMaxHr: true });
            } else {
                AC.get('maxHr').setErrors(null);
            }
        }
        return null;
    }

    checkSingleQuote(AC: AbstractControl) {
        let newText = AC.value;

        if (newText != undefined) {
            if (typeof newText == "string") {
                if (newText.includes('\''))
                    return { forbiddenChar: true }

                if (newText.includes('\"'))
                    return { forbiddenChar: true }

                if (newText.includes('\/'))
                    return { forbiddenChar: true }

                if (newText.includes('\\'))
                    return { forbiddenChar: true }
            }
        }
        return null;
    }

    checkDateRange(AC: AbstractControl) {
      const dateFrom = AC.get('dateFrom')?.value;
      const dateTo = AC.get('dateTo')?.value;

      // tylko waliduj gdy oba pola mają kompletne daty
      if (dateFrom && dateTo && dateFrom.year && dateFrom.month && dateFrom.day
        && dateTo.year && dateTo.month && dateTo.day) {
        const from = new Date(dateFrom.year, dateFrom.month - 1, dateFrom.day);
        const to = new Date(dateTo.year, dateTo.month - 1, dateTo.day);

        if (from.getTime() > to.getTime()) {
          AC.get('dateFrom').setErrors({ dateRangeInvalid: true });
          return null;
        }
      }

      // wyczyść błąd dateRangeInvalid jeśli istnieje, ale zachowaj inne błędy
      ['dateFrom', 'dateTo'].forEach(field => {
        const control = AC.get(field);
        if (control?.hasError('dateRangeInvalid')) {
          const errors = { ...control.errors };
          delete errors['dateRangeInvalid'];
          control.setErrors(Object.keys(errors).length ? errors : null);
        }
      });

      return null;
    }

    checkAlphanumeric(AC: AbstractControl) {
        const value = AC.value;
        if (value && typeof value === 'string') {
            const regex = /^[a-zA-Z0-9 ]*$/;
            if (!regex.test(value)) {
                return { alphanumeric: true };
            }
        }
        return null;
    }
}
